/**
 * Provides information about current page to any component.
 */
import { PropsWithChildren, createContext, memo, use, useMemo, useRef } from 'react';

export interface PrevPageInfo {
	prev_page_id: string;
	prev_page_title: string;
	prev_page_URL: string;
	prev_page_store_title: string;
	prev_page_store_id: string;
	prev_page_store_slug: string;
	prev_page_document_type: string;
	prev_page_offering_type: string;
	prev_page_price: string;
	prev_page_currency: string;
	prev_page_seller_id?: string;
	prev_page_seller_title?: string;
	prev_page_fulfiller_id?: string;
	prev_page_fulfiller_title?: string;
}

export interface CurrentPageInfo {
	current_page_id: string;
	current_page_title: string;
	current_page_URL: string;
	current_page_store_title: string;
	current_page_store_id: string;
	current_page_store_slug: string;
	current_page_document_type: string;
	current_page_offering_type: string;
	current_page_price: string;
	current_page_currency: string;
	current_page_seller_id?: string;
	current_page_seller_title?: string;
	current_page_fulfiller_id?: string;
	current_page_fulfiller_title?: string;
}

type CurrentPageInfoContextProps = {
	getCurrentPageInfo: () => CurrentPageInfo;
	setCurrentPageInfo: (pageInfo: CurrentPageInfo) => void;
	getPrevPageInfo: () => PrevPageInfo;
	setPrevPageInfo: (currentPageInfo: CurrentPageInfo) => void;
};

const CurrentPageInfoContext = createContext<CurrentPageInfoContextProps | null>(null);

export const useCurrentPageInfo = () => {
	const context = use(CurrentPageInfoContext);
	if (!context) {
		throw new Error('useCurrentPageInfo must be used within CurrentPageInfoProvider.');
	}
	return context;
};

export const CurrentPageInfoProvider = memo(({ children }: PropsWithChildren) => {
	// See https://onetakeda.atlassian.net/browse/MKT-971
	const currentPageInfoRef = useRef<CurrentPageInfo>(createEmptyCurrentPageInfo());
	const prevPageInfoRef = useRef<PrevPageInfo>(createEmptyPrevPageInfo());

	const value = useMemo<CurrentPageInfoContextProps>(
		() => ({
			getCurrentPageInfo: () => currentPageInfoRef.current,
			setCurrentPageInfo: (pageInfo) => (currentPageInfoRef.current = pageInfo),
			getPrevPageInfo: () => prevPageInfoRef.current,
			setPrevPageInfo: (currentPageInfo: CurrentPageInfo) =>
				(prevPageInfoRef.current = {
					prev_page_id: currentPageInfo.current_page_id,
					prev_page_title: currentPageInfo.current_page_title,
					prev_page_URL: currentPageInfo.current_page_URL,
					prev_page_store_title: currentPageInfo.current_page_store_title,
					prev_page_store_id: currentPageInfo.current_page_store_id,
					prev_page_store_slug: currentPageInfo.current_page_store_slug,
					prev_page_document_type: currentPageInfo.current_page_document_type,
					prev_page_offering_type: currentPageInfo.current_page_offering_type,
					prev_page_price: currentPageInfo.current_page_price,
					prev_page_currency: currentPageInfo.current_page_currency,
				}),
		}),
		[],
	);

	return <CurrentPageInfoContext value={value}>{children}</CurrentPageInfoContext>;
});

export const createEmptyCurrentPageInfo: () => CurrentPageInfo = () => {
	return {
		current_page_id: '',
		current_page_title: '',
		current_page_URL: '',
		current_page_store_title: '',
		current_page_store_id: '',
		current_page_store_slug: '',
		current_page_document_type: '',
		current_page_offering_type: '',
		current_page_price: '',
		current_page_currency: '',
	};
};

export const createEmptyPrevPageInfo: () => PrevPageInfo = () => {
	return {
		prev_page_id: '',
		prev_page_title: '',
		prev_page_URL: '',
		prev_page_store_title: '',
		prev_page_store_id: '',
		prev_page_store_slug: '',
		prev_page_document_type: '',
		prev_page_offering_type: '',
		prev_page_price: '',
		prev_page_currency: '',
	};
};

CurrentPageInfoProvider.displayName = 'CurrentPageInfoProvider';
